import { analytics, EventEnum } from "@/lib/analytics";
import { apiClient, setTokenProvider } from "@/lib/lutely-api/api-request";
import {
  useAuthControllerSignUp,
  useAuthControllerOtpInit,
} from "@/lib/lutely-api/gen/auth/auth";
import { SignUpDto } from "@/lib/lutely-api/gen/dtos/signUpDto";
import {
  clearRedirect,
  getMostRecentRoute,
  getRedirect,
  saveCodeRequestTimestamp,
  saveLastAuth,
} from "@/lib/storage-utils";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import ApiContext from "./ApiContext";
import { signIn, fetchAuthSession, signOut } from "aws-amplify/auth";
import {
  sendUserEmailVerification,
  pendingEmailVerificationRedirect,
} from "@/lib/auth";

const ADMIN_GROUP_NAME = "Admins";

interface ApiProviderProps {
  children: ReactNode;
}

function ApiProvider({ children }: ApiProviderProps) {
  const { mutateAsync: signUpMutation } = useAuthControllerSignUp();
  const { mutateAsync: otpInitMutation } = useAuthControllerOtpInit();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [apiClientConfigured, setApiClientConfigured] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const pauseApi = useCallback((paused: boolean) => {
    setIsPaused(paused);
  }, []);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Logs out the user and redirects to the login page
  const logoutUser = useCallback(async () => {
    analytics.reset();
    await signOut();
    location.replace("/");
  }, []);

  // Retrieves the JWT token and updates authentication state
  // If token retrieval errors, logs out the user
  const getTokens = useCallback(
    async (forceRefresh: boolean = false) => {
      try {
        const { tokens } = await fetchAuthSession({ forceRefresh });
        const access = tokens?.accessToken?.toString();
        const id = tokens?.idToken?.toString();

        setIsAuthenticated(Boolean(access));

        const groups = tokens?.accessToken?.payload?.["cognito:groups"];
        setIsAdmin(Array.isArray(groups) && groups.includes(ADMIN_GROUP_NAME));

        return { access, id };
      } catch {
        setIsAuthenticated(false);
        setIsAdmin(false);
        await logoutUser();
        return { access: undefined, id: undefined };
      }
    },
    [logoutUser],
  );

  const getRedirectPath = useCallback((): {
    pathname: string;
    search: object;
  } => {
    const redirectLocation = getRedirect();
    const mostRecentLocation = getMostRecentRoute();
    if (redirectLocation?.pathname) {
      clearRedirect();
      return redirectLocation;
    }

    if (mostRecentLocation?.pathname) {
      return mostRecentLocation;
    }

    return { pathname: "/audiolab", search: {} };
  }, []);

  // Logs in the user and redirects to the home page on success
  const loginUser = useCallback(
    async (
      email: string,
      password: string,
      skipNavigation: boolean = false,
    ) => {
      try {
        const output = await signIn({
          username: email,
          password: password,
        });

        // Doesn't apply to us but in the future the value would be different for MFA or other sign in flows
        if (output.nextStep.signInStep === "DONE") {
          try {
            await fetchAuthSession({ forceRefresh: true });
          } catch (sessionError) {
            console.warn(
              "Failed to refresh session immediately after sign-in:",
              sessionError,
            );
          }
          analytics.track(EventEnum.USER_LOGGED_IN, {
            method: "email",
          });
          saveLastAuth("otp", email);

          setIsAuthenticated(true);
          // Anything fetched while the visitor was anonymous (album detail with
          // preview audio, empty order lists) is now wrong for this user.
          queryClient.invalidateQueries();

          if (!skipNavigation) {
            const pendingVerification = pendingEmailVerificationRedirect(email);
            if (pendingVerification) {
              window.location.replace(pendingVerification);
              return output;
            }

            const { pathname, search } = getRedirectPath();
            sessionStorage.setItem("justLoggedIn", "true");
            navigate({
              to: pathname,
              search,
              replace: true,
            });
          }
        }

        return output;
      } catch (err: unknown) {
        console.error("Login failed:", err);
        throw err;
      }
    },
    [navigate, getRedirectPath, setIsAuthenticated, queryClient],
  );

  const otcLoginUser = useCallback(
    async (_email: string, accessToken: string, idToken: string) => {
      await otpInitMutation({ data: { accessToken, idToken } });

      analytics.track(EventEnum.USER_LOGGED_IN, {
        method: "otp",
      });
      saveLastAuth("otp", _email);

      setIsAuthenticated(true);
      queryClient.invalidateQueries();

      const pendingVerification = pendingEmailVerificationRedirect(_email);
      if (pendingVerification) {
        window.location.replace(pendingVerification);
        return;
      }

      const { pathname, search } = getRedirectPath();
      sessionStorage.setItem("justLoggedIn", "true");
      navigate({
        to: pathname,
        search,
        replace: true,
      });
    },
    [navigate, getRedirectPath, otpInitMutation, queryClient],
  );

  // Signs up the user, then logs them in
  const signUpUser = useCallback(
    async (signUpDto: SignUpDto, skipNavigation: boolean = false) => {
      try {
        await signUpMutation({ data: signUpDto });
        setIsPaused(true);
        await loginUser(signUpDto.email, signUpDto.password, true);
        await sendUserEmailVerification();
        saveCodeRequestTimestamp(signUpDto.email);

        if (skipNavigation) {
          setIsPaused(false);
          return;
        }

        // No "check your inbox" toast — the verification gate says it.
        const { pathname, search } = getRedirectPath();
        navigate({
          to: pathname,
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            ...search,
          }),
          replace: true,
          reloadDocument: true,
        });
      } catch (error: unknown) {
        setIsPaused(false);
        console.error("Sign up failed:", error);
        throw error;
      }
    },
    // Ensure all necessary dependencies are here
    [loginUser, signUpMutation, navigate, getRedirectPath],
  );

  // The request interceptor that attaches the JWT lives at module scope in
  // api-request.ts so that requests made before this provider mounts (router
  // loaders, queries started in child effects) are authenticated too. Here we
  // only swap in a provider that additionally keeps React auth state in sync
  // and logs the user out when the session can no longer be resolved.
  useEffect(() => {
    setTokenProvider(() => getTokens());

    const responseInterceptor = apiClient.interceptors.response.use(
      async function onFulfilled(response) {
        // Handle successful responses
        return response;
      },
      async function onRejected(error) {
        const status = error?.response?.status;

        // Report API errors to DD RUM for session-level visibility
        if (status && status >= 500) {
          datadogRum.addError(
            new Error(
              `API ${error.config?.method?.toUpperCase()} ${error.config?.url} returned ${status}`,
            ),
            {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase(),
              status,
            },
          );
        }

        // TODO: Global error handling (e.g., log out on 401)
        return Promise.reject(error);
      },
    );

    setApiClientConfigured(true);

    // Clean up on component unmount
    return () => {
      setTokenProvider(null);
      apiClient.interceptors.response.eject(responseInterceptor);
      setApiClientConfigured(false);
    };
  }, [getTokens]);

  // On component mount, check if the user is authenticated
  useEffect(() => {
    getTokens();
  }, [getTokens]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      loginUser,
      otcLoginUser,
      signUpUser,
      logoutUser,
      isAuthenticated,
      apiClientConfigured,
      getTokens,
      isAdmin,
      pauseApi,
      isPaused,
      getRedirectPath,
    }),
    [
      loginUser,
      otcLoginUser,
      signUpUser,
      logoutUser,
      isAuthenticated,
      apiClientConfigured,
      getTokens,
      isAdmin,
      pauseApi,
      isPaused,
      getRedirectPath,
    ],
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export default ApiProvider;
